import { captureException } from '@sentry/browser';
import { RenderPreappDebugVar } from 'src/contexts/messages/render-preapp-debug';
import '@motional/inguitiv/fonts.css';
import 'src/index.scss';

// Do not import ApiError (or anything App also needs) into this entry module.
// index top-level-awaits a dynamic import of App; if App statically imports a
// binding that Rollup placed on this entry chunk, module evaluation deadlocks
// and the UI stays on “Initializing…”.

const RENDER_PREAPP_DEBUG_TEXT: RenderPreappDebugVar =
  '__RENDER_PREAPP_DEBUG_TEXT__';

const renderLog = (message: string) => {
  if (!window[RENDER_PREAPP_DEBUG_TEXT]) return;

  const logElt = document.createElement('div');
  logElt.innerHTML = message;

  logElt.style.pointerEvents = 'none';

  logElt.style.position = 'relative';
  logElt.style.zIndex = String(Math.pow(2, 52));
  document.body.appendChild(logElt);
};

renderLog('The index file was parsed');

function reloadPage(event: MouseEvent) {
  if (
    !event.target ||
    !('closest' in event.target) ||
    typeof event.target.closest !== 'function'
  ) {
    return;
  }

  if (event.target.closest('#toggle_reloadPageNormally')) {
    const url = new URL(globalThis.location.href);
    url.searchParams.delete(RENDER_PREAPP_DEBUG_TEXT);
    globalThis.location.href = url.toString();
    captureException(new Error('User reloaded without debugger'));
  }

  if (event.target.closest('#toggle_reloadPageWithDebugger')) {
    const url = new URL(globalThis.location.href);
    url.searchParams.set(RENDER_PREAPP_DEBUG_TEXT, '');
    globalThis.location.href = url.toString();
    captureException(new Error('User reloaded with debugger'));
  }
}

async function init() {
  renderLog('Initializing function opened');

  const rootContainer = document.getElementById('root')!;

  if (rootContainer) {
    renderLog('Found root container');
  } else {
    renderLog('Could not find root container');
    return;
  }

  rootContainer.innerHTML = `
      <style>
        @keyframes rotate-infinity {
          from { stroke-dashoffset: 115 }
          to { stroke-dashoffset: 230 }
        }
        @keyframes fade-in {
          from { opacity: 0 }
          to { opacity: 1 }
        }
      </style>

      <div style="animation: 4s ease-in-out fade-in; padding: 32px; text-align: center">
        <h1 style="font-size: 24px">Initializing…</h1>

        <svg fill="none" viewBox="0 0 44 24" width="90" style="margin-top: 16px">
          <path style="
            --dist: 115;
            stroke-width: 6;
            stroke: #5c48f6;
            stroke-dasharray: 0 115 calc(115 / 4)
            calc((115 / 4) * 3) calc(115 / 4)
            calc((115 / 4) * 3);

            animation-name: rotate-infinity;
            animation-iteration-count:infinite;
            animation-duration: 1.3s;
            animation-timing-function: linear;
          " d="M18.364 5.636c4.5 4.5 2.772 8.228 7.272 12.728a9 9 0 1 0 0-12.728c-4.5 4.5-2.772 8.228-7.272 12.728a9 9 0 1 1 0-12.728Z" />
        </svg>

        <div
          style="
            animation: 4s linear 3s backwards fade-in;
            display: grid;
            gap: var(--gap-m);
            justify-content: center;
            justify-items: center;
          "
        >
          <p>This is taking longer than normal…</p>
          <button
            id="toggle_reloadPageNormally"
            style="
              background-color: #5c48f6;
              font-weight: 600;
              text-align: center;
              text-box-edge: cap alphabetic;
              text-box-trim: trim-both;
              padding: var(--gap-m);
              border-radius: 8px;
              cursor: pointer;
            "
          >
            Click here to reload
          </button>

          <button
            id="toggle_reloadPageWithDebugger"
            style="color: #5b5f67"
          >
            Click here to reload with debugger
          </button>
        </div>
      </div>
    `;

  globalThis.addEventListener('click', reloadPage);

  try {
    renderLog('Injected root container with loading DOM');

    renderLog('Began loading modules…');

    const [reactDom, appScreens, outdatedBrowserCheck, windowSizeCheck] =
      await Promise.all([
        import('react-dom/client').catch((error) => {
          renderLog('Loading module “react-dom” failed:');
          renderLog(` — ${error.toString()}`);

          throw error;
        }),
        import('src/screens/App').catch((error) => {
          renderLog('Loading module “App” failed:');
          renderLog(` — ${error.toString()}`);

          throw error;
        }),
        import('./tools/check-outdated-browser').catch((error) => {
          renderLog('Loading module “outdatedBrowserCheck” failed:');
          renderLog(` — ${error.toString()}`);

          throw error;
        }),
        import('./tools/window-size').catch((error) => {
          renderLog('Loading module “windowSizeCheck” failed:');
          renderLog(` — ${error.toString()}`);

          throw error;
        }),
      ]);

    const { createRoot } = reactDom;
    const { default: App } = appScreens;
    const { alertAboutOutdatedBrowsers } = outdatedBrowserCheck;
    const { updateWindowSizeStyles } = windowSizeCheck;

    renderLog('Modules loaded.');

    if (typeof globalThis.window !== 'undefined') {
      globalThis.global ??= globalThis;
    }
    renderLog('Ensured global object exists');

    alertAboutOutdatedBrowsers();
    renderLog('Alerted about outdated browsers');

    const startAppWithMockedNetwork = async () => {
      const { worker } = await import('./mocks/browser');

      await worker.start({
        onUnhandledRequest: 'bypass',
        serviceWorker: { url: '/mockServiceWorker.js' },
      });
    };
    renderLog('Added “start with mock network” function');

    // Workaround: Currently, we cannot run 2 service workers,
    // so we will not register for push notification when running "mocking mode"
    // After fully mock (even okta), we can change to default port to avoid the service worker collision
    // TODO: RTI-4005 find a way to merge the service workers / run two at once
    const mountApp = () => {
      // clear the loader
      rootContainer.innerHTML = '';

      const root = createRoot(rootContainer);
      root.render(<App />);
    };
    renderLog('Added “mount app” function');

    if (import.meta.env.VITE_MSW_ENABLED) {
      renderLog('Starting with mock network');
      startAppWithMockedNetwork().then(() => {
        renderLog('Mock network started');
        mountApp();
        renderLog('Mounted app');
      });
    } else {
      renderLog('Starting with all-real network');
      mountApp();
      renderLog('Mounted app');
    }

    updateWindowSizeStyles();
    renderLog('Updated window size styles');
  } catch (error) {
    renderLog(
      `
      <div style="padding: 32px">
        <p style="text-align: center; font-size: 24px; font-weight: bold">Something went wrong on our end.</p>
        <p style="text-align: center">If this keeps happening, contact support.</p>
        <hr style="border-top: 1px solid #798d8d; margin: 32px;">
        <p>Details:</p>
        ${
          error instanceof Error ?
            `<ul>
              ${error?.message ? `<li style="list-style: disc">${error.message}</li>` : ''}
              ${error?.stack ? `<li style="list-style: disc">${error.stack}</li>` : ''}
              ${
                error &&
                typeof error === 'object' &&
                'code' in error &&
                error.code ?
                  `<li style="list-style: disc">${String(error.code)}</li>`
                : ''
              }
            </ul>`
          : `<p>Error: ${String(error)}</p>`
        }
      </div>
      <hr>
    `,
    );

    throw error;
  }
}

renderLog('The index file ran');

init().then(() => {
  renderLog('The index file ran to the end');
});
